/* eslint-disable @typescript-eslint/naming-convention */
import { useMemo, VFC } from 'react';
import { Collapse, Descriptions, Space, Tag } from 'antd';
import dayjs from 'dayjs';
import {
  accessibilityTranslated,
  deviceCategoryTranslated,
  installationPositionTranslated,
  orientationTranslated,
  commodityTranslated,
} from 'components/Form/meter_details/constants';
import { useInventoryMeterReadingGroups } from 'hooks/api/reading-groups/useFind';
import { Link } from 'react-router-dom';
import { FormattedMessage, IntlShape } from 'react-intl';
import {
  Collection,
  License,
  MeterAccessibility,
  MeterDetailElectricity,
  MeterDetailGas,
  MeterDetailThermal,
  MeterDetailOther,
  MeterDetailWater,
  SerializedMeter,
} from '@enmon/commons/lib/enmon';
import { SerializedInventoryMeter } from '@enmon/commons/lib/tools';
import { DIAMETER_NOMINAL_LABELS, Nullable, RESISTANCE_LABELS } from '@enmon/commons';
import { useFiles } from 'hooks/api/enmon-tools/files/useFiles';
import { Commodity } from '@enmon/commons/lib/shared';
import { negatePositiveNumber } from '../../../utils/math';
import { DETAIL_DESCRIPTION_CONFIG } from '../const';
import { Gallery } from '../../../components/Gallery/Gallery';
import { InventoryMeterImage } from '../../../components/Image/InventoryMeter';
import { MeterHardwares } from './Hardwares';
import { InformationTab as informationTabMessages, MeterDetail as meterDetailMessages } from '../messages';

const { Panel } = Collapse;

function getAdditionalFields(meter: SerializedMeter | undefined, intl: IntlShape) {
  const detail = meter?.detail ?? {};

  switch (meter?.commodity) {
    case Commodity.Thermal: {
      const { diameterNominal, flowrate, installationPosition, layLength, orientation, resistance } =
        detail as MeterDetailThermal;

      const flowratePrefix = flowrate?.slice(0, 2);
      const parsedFlowrate = flowrate?.slice(2);

      return (
        <>
          <Descriptions.Item
            label={<FormattedMessage {...meterDetailMessages.flowRate} values={{ unit: flowratePrefix ?? 'Q3' }} />}
          >
            {parsedFlowrate && `${parsedFlowrate} m${String.fromCharCode(179)}/h`}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage {...meterDetailMessages.layLength} />}>
            {layLength && `${layLength} mm`}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage {...meterDetailMessages.diameterNominal} />}>
            {diameterNominal && DIAMETER_NOMINAL_LABELS[diameterNominal]}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage {...meterDetailMessages.orientation} />}>
            {orientation && intl.formatMessage(orientationTranslated[orientation])}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage {...meterDetailMessages.resistance} />}>
            {resistance && RESISTANCE_LABELS[resistance]}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage {...meterDetailMessages.installationPosition} />}>
            {installationPosition && intl.formatMessage(installationPositionTranslated[installationPosition])}
          </Descriptions.Item>
        </>
      );
    }

    case Commodity.Water: {
      const { diameterNominal, flowrate, layLength, orientation } = detail as MeterDetailWater;

      const flowratePrefix = flowrate?.slice(0, 2);
      const parsedFlowrate = flowrate?.slice(2);

      return (
        <>
          <Descriptions.Item
            label={<FormattedMessage {...meterDetailMessages.flowRate} values={{ unit: flowratePrefix ?? 'Q3' }} />}
          >
            {parsedFlowrate && `${parsedFlowrate} m${String.fromCharCode(179)}/h`}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage {...meterDetailMessages.layLength} />}>
            {layLength && `${layLength} mm`}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage {...meterDetailMessages.diameterNominal} />}>
            {diameterNominal && DIAMETER_NOMINAL_LABELS[diameterNominal]}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage {...meterDetailMessages.orientation} />}>
            {orientation && intl.formatMessage(orientationTranslated[orientation])}
          </Descriptions.Item>
        </>
      );
    }
    case Commodity.Electricity: {
      const { deviceCategory, isDirect, measuringCurrentTransformer, measurementCategory } =
        detail as MeterDetailElectricity;

      return (
        <>
          <Descriptions.Item label={<FormattedMessage {...meterDetailMessages.equipmentCategory} />}>
            {deviceCategory && intl.formatMessage(deviceCategoryTranslated[deviceCategory])}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage {...meterDetailMessages.measurementCategory} />}>
            {measurementCategory}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage {...meterDetailMessages.directElectricityMeter} />}>
            {isDirect ? (
              <FormattedMessage {...meterDetailMessages.direct} />
            ) : (
              <FormattedMessage {...meterDetailMessages.indirect} />
            )}
          </Descriptions.Item>
          <Descriptions.Item label="MCT">{measuringCurrentTransformer}</Descriptions.Item>
        </>
      );
    }
    case Commodity.NaturalGas: {
      const { gSize, pMax, qMax, qMin, diameterNominal, deviceCategory, measurementCategory } =
        detail as MeterDetailGas;

      return (
        <>
          <Descriptions.Item label={<FormattedMessage {...meterDetailMessages.equipmentCategory} />}>
            {deviceCategory && intl.formatMessage(deviceCategoryTranslated[deviceCategory])}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage {...meterDetailMessages.measurementCategory} />}>
            {measurementCategory}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage {...meterDetailMessages.gSize} />}>
            {gSize} {`m${String.fromCharCode(179)}/h`}
          </Descriptions.Item>
          <Descriptions.Item label="Pmax">{pMax} MPa</Descriptions.Item>
          <Descriptions.Item label="Qmax">
            {qMax} {`m${String.fromCharCode(179)}/h`}
          </Descriptions.Item>
          <Descriptions.Item label="Qmin">
            {qMin} {`m${String.fromCharCode(179)}/h`}
          </Descriptions.Item>
          <Descriptions.Item label={<FormattedMessage {...meterDetailMessages.diameterNominal} />}>
            {diameterNominal && DIAMETER_NOMINAL_LABELS[diameterNominal]}
          </Descriptions.Item>
        </>
      );
    }
    case Commodity.Other: {
      const { deviceCategory } = detail as MeterDetailOther;

      return (
        <Descriptions.Item label={<FormattedMessage {...meterDetailMessages.equipmentCategory} />}>
          {deviceCategory && intl.formatMessage(deviceCategoryTranslated[deviceCategory])}
        </Descriptions.Item>
      );
    }
    default:
      console.error('Unsupported commodity');
      return null;
  }
}

interface InformationTabProps {
  enmonMeter: Nullable<SerializedMeter>;
  inventoryMeter: Nullable<SerializedInventoryMeter>;
  intl: IntlShape;
}

export const InformationTab: VFC<InformationTabProps> = ({ enmonMeter, inventoryMeter, intl }) => {
  const { updatedAt, commodity, owner, serial, detail, accessibility, manufactured, calibrated, name } =
    enmonMeter ?? {};
  const { submitted_at, submitted_by, installed_at, installed_by, lorawan_rssi, nbiot_rssi, internal_note } =
    inventoryMeter ?? {};

  const inventoryMeterId = inventoryMeter?.id;
  const [lat, lng] = enmonMeter?.location?.coordinates ?? [null, null];

  const { data: groupResponse } = useInventoryMeterReadingGroups(
    { filter: { inventoryMeter: inventoryMeterId } },
    { enabled: !!inventoryMeterId },
  );

  const { data: useFilesResult } = useFiles({
    collections: [Collection.Meters],
    meters: [enmonMeter?._id ?? ''],
  });

  const files = useFilesResult?.result ?? [];

  const groups = useMemo(() => groupResponse?.data ?? [], [groupResponse]);

  const additionalFields = useMemo(() => getAdditionalFields(enmonMeter as SerializedMeter, intl), [enmonMeter, intl]);

  if (!enmonMeter || !inventoryMeter) {
    return null;
  }

  const licence = enmonMeter.registers[0]?.lastMessage?.origin;

  return (
    <Space className="w-100" direction="vertical">
      <Descriptions bordered column={DETAIL_DESCRIPTION_CONFIG}>
        <Descriptions.Item label="Strapi ID">{inventoryMeterId ?? ''}</Descriptions.Item>
        <Descriptions.Item label={<FormattedMessage {...informationTabMessages.updatedAt} />}>
          {updatedAt ? dayjs(updatedAt).format('DD.MM.YYYY HH:mm') : ''}
        </Descriptions.Item>
        <Descriptions.Item label={<FormattedMessage {...informationTabMessages.commodity} />}>
          {commodity ? intl.formatMessage(commodityTranslated[commodity]) : ''}
        </Descriptions.Item>
        <Descriptions.Item label={<FormattedMessage {...informationTabMessages.monitoredEntity} />}>
          {name ?? ''}
        </Descriptions.Item>
        <Descriptions.Item label={<FormattedMessage {...informationTabMessages.owner} />}>
          {owner ?? ''}
        </Descriptions.Item>
        <Descriptions.Item label={<FormattedMessage {...informationTabMessages.serialNumber} />} span={24}>
          {serial ?? ''}
        </Descriptions.Item>
        <Descriptions.Item label={<FormattedMessage {...informationTabMessages.pasportDate} />}>
          {submitted_at ? dayjs(submitted_at).format('DD.MM.YYYY HH:mm') : ''}
        </Descriptions.Item>
        <Descriptions.Item label={<FormattedMessage {...informationTabMessages.pasportBy} />}>
          {submitted_by ?? ''}
        </Descriptions.Item>
        <Descriptions.Item label={<FormattedMessage {...informationTabMessages.installationDate} />}>
          {installed_at ? dayjs(installed_at).format('DD.MM.YYYY HH:mm') : ''}
        </Descriptions.Item>
        <Descriptions.Item label={<FormattedMessage {...informationTabMessages.installedBy} />}>
          {installed_by ?? ''}
        </Descriptions.Item>
        <Descriptions.Item label={<FormattedMessage {...informationTabMessages.license} />}>
          {enmonMeter.license === License.Offline && <Tag>Offline</Tag>}
          {enmonMeter.license === License.Online && (
            <>
              <Tag color="green">Online</Tag>
              {licence && ` [${licence}]`}
            </>
          )}
        </Descriptions.Item>
        <Descriptions.Item label={<FormattedMessage {...informationTabMessages.lastUpdate} />}>
          {enmonMeter.lastUpdate ? dayjs(enmonMeter.lastUpdate).format('DD.MM.YYYY HH:mm') : ''}
        </Descriptions.Item>
      </Descriptions>

      <Collapse>
        <Panel key="1" header={<FormattedMessage {...informationTabMessages.details} />}>
          <Descriptions bordered={false} column={DETAIL_DESCRIPTION_CONFIG}>
            <Descriptions.Item label={<FormattedMessage {...informationTabMessages.serialNumber} />}>
              {serial}
            </Descriptions.Item>
            {detail ? additionalFields : null}
            <Descriptions.Item label={<FormattedMessage {...informationTabMessages.lorawanRssi} />}>
              {lorawan_rssi
                ? `${negatePositiveNumber(lorawan_rssi) ?? ''} dB`
                : intl.formatMessage(informationTabMessages.unmeasured)}
            </Descriptions.Item>
            <Descriptions.Item label={<FormattedMessage {...informationTabMessages.nbiotRssi} />}>
              {nbiot_rssi
                ? `${negatePositiveNumber(nbiot_rssi) ?? ''} dB`
                : intl.formatMessage(informationTabMessages.unmeasured)}
            </Descriptions.Item>
            <Descriptions.Item label={<FormattedMessage {...informationTabMessages.accessibility} />}>
              {accessibility
                ? accessibility
                    .map(access => access.split(';'))
                    .flat()
                    .filter(access => accessibilityTranslated[access as MeterAccessibility])
                    .map(access => intl.formatMessage(accessibilityTranslated[access as MeterAccessibility]))
                    .join(', ')
                : null}
            </Descriptions.Item>
            <Descriptions.Item label="Lat">{lat}</Descriptions.Item>
            <Descriptions.Item label="Lng">{lng}</Descriptions.Item>
            <Descriptions.Item label={<FormattedMessage {...informationTabMessages.manufactured} />}>
              {manufactured}
            </Descriptions.Item>
            <Descriptions.Item label={<FormattedMessage {...informationTabMessages.calibrated} />}>
              {calibrated}
            </Descriptions.Item>
            <Descriptions.Item label={<FormattedMessage {...informationTabMessages.note} />}>
              {internal_note}
            </Descriptions.Item>
          </Descriptions>
        </Panel>
      </Collapse>
      <Collapse>
        <Panel key="groups" header={<FormattedMessage {...informationTabMessages.group} />}>
          {groups.length > 0 ? (
            <ul>
              {groups.map(group => (
                <li key={group.id}>
                  <Link to={`/reading-groups/${group.id}`}>{group.name}</Link>
                </li>
              ))}
            </ul>
          ) : (
            <div>
              <FormattedMessage {...informationTabMessages.noReadingGroup} />
            </div>
          )}
        </Panel>
      </Collapse>
      <Collapse>
        <Panel key="hardwares" header={<FormattedMessage {...informationTabMessages.assignedHardware} />}>
          <MeterHardwares />
        </Panel>
      </Collapse>
      <Gallery>
        {files.map(file => (
          <InventoryMeterImage key={file._id} file={file} />
        ))}
      </Gallery>
    </Space>
  );
};
